import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { marcas as apiMarcas, type MarcaNaLista } from '../api/marcas'
import { useSessao } from '../auth/useSessao'
import { useTema } from '../tema/useTema'
import { AjudaDaTela } from './AjudaDaTela'
import { Botao } from './Botao'
import './casca.css'

/**
 * A casca do portal: barra superior e menu à esquerda.
 *
 * 🔴 **Nenhuma chave de marca aparece aqui.** Os blocos do menu são montados a
 * partir de `GET /marcas` — antes desta demanda, `digiorgio` estava escrito à
 * mão em cinco botões da página inicial, e a Letras e Versos existia no banco
 * sem nenhuma forma de ser aberta pela interface.
 *
 * O genérico fica no topo porque não pertence a marca nenhuma: usuários,
 * servidor de e-mail e auditoria seriam órfãos numa árvore por marca.
 *
 * `AUT-06`: esconder item de menu é **conforto**, não autorização — quem barra
 * é o servidor. O teste que prova isso chama a rota escondida e espera 403.
 */
export function Casca({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { usuario, pode, sair } = useSessao()
  const { temaEfetivo, preferencia, definirPreferencia } = useTema()

  const [lista, setLista] = useState<MarcaNaLista[] | null>(null)
  const [gavetaAberta, setGavetaAberta] = useState(false)

  useEffect(() => {
    if (!pode('ParametroVer')) {
      setLista([])
      return
    }

    apiMarcas
      .listar()
      .then(setLista)
      // Menu que não carrega não pode derrubar a página: o conteúdo continua
      // acessível pela URL, e o erro aparece como ausência de blocos de marca.
      .catch(() => setLista([]))
  }, [pode])

  const caminho = window.location.pathname

  return (
    <div className="casca">
      <header className="casca__topo">
        <button
          type="button"
          className="casca__gaveta"
          aria-expanded={gavetaAberta}
          aria-controls="menu-do-portal"
          onClick={() => setGavetaAberta((antes) => !antes)}
        >
          <span aria-hidden="true">☰</span>
          <span className="casca__so-leitor">{t('nav.abrirMenu')}</span>
        </button>

        <a className="casca__marca" href="/">
          {t('app.titulo')}
        </a>

        <div className="casca__topo-acoes">
          {/* 🔴 Na casca, e não em cada tela: é o que faz a ajuda estar onde a
              dúvida nasce, em todo lugar, sem depender de alguém lembrar de
              acrescentá-la. Some sozinha quando a rota não tem verbete. */}
          <AjudaDaTela />

          <span className="casca__usuario">
            {t('portal.ola', { nome: usuario?.nome ?? '' })}
          </span>

          <Botao
            variante="discreto"
            onClick={() => definirPreferencia(preferencia === 'escuro' ? 'claro' : 'escuro')}
          >
            {t('tema.alternar')}
            {' · '}
            {t(`tema.${temaEfetivo}`)}
          </Botao>

          <Botao variante="discreto" onClick={sair}>
            {t('acesso.sair')}
          </Botao>
        </div>
      </header>

      <div className="casca__corpo">
        <nav
          id="menu-do-portal"
          aria-label={t('nav.rotulo')}
          className={`casca__menu ${gavetaAberta ? 'casca__menu--aberto' : ''}`}
        >
          <ul className="casca__lista">
            <Item href="/" rotulo={t('nav.inicio')} atual={caminho} />

            {pode('ParametroVer') && (
              <Item href="/marcas" rotulo={t('nav.marcas')} atual={caminho} />
            )}
            {pode('ParametroVer') && (
              <Item href="/times" rotulo={t('nav.times')} atual={caminho} />
            )}
            {pode('ParametroVer') && (
              <Item href="/regras" rotulo={t('nav.regras')} atual={caminho} />
            )}
            {pode('AcervoVer') && (
              <Item href="/contatos" rotulo={t('portal.contatos')} atual={caminho} />
            )}
            {pode('AcervoVer') && (
              <Item href="/acervo" rotulo={t('portal.acervo')} atual={caminho} />
            )}
            {pode('AvaliacaoVer') && (
              <Item href="/avaliacoes" rotulo={t('portal.avaliacoes')} atual={caminho} />
            )}
            {pode('UsuarioVer') && (
              <Item href="/usuarios" rotulo={t('portal.usuarios')} atual={caminho} />
            )}
            {pode('ParametroVer') && (
              <Item href="/ia/consumo" rotulo={t('portal.ia')} atual={caminho} />
            )}
            {pode('ParametroVer') && (
              <Item href="/tabela-de-precos" rotulo={t('portal.tabela')} atual={caminho} />
            )}
            {pode('ParametroVer') && (
              <Item href="/configuracao/ia" rotulo={t('nav.chaves')} atual={caminho} />
            )}
            {pode('ParametroVer') && (
              <Item href="/configuracao/whatsapp" rotulo={t('nav.modelosWhatsapp')} atual={caminho} />
            )}
            {pode('ParametroVer') && (
              <Item
                href="/configuracao/transporte"
                rotulo={t('nav.transporte')}
                atual={caminho}
              />
            )}
            {pode('ParametroVer') && (
              <Item
                href="/configuracao/embalagens"
                rotulo={t('embalagens.titulo')}
                atual={caminho}
              />
            )}
            {pode('ParametroVer') && (
              <Item
                href="/configuracao/prazos"
                rotulo={t('prazos.titulo')}
                atual={caminho}
              />
            )}
            {pode('ParametroVer') && (
              <Item
                href="/configuracao/publicacao"
                rotulo={t('publicacao.titulo')}
                atual={caminho}
              />
            )}
            {pode('SmtpGerenciar') && (
              <Item href="/configuracao/email" rotulo={t('portal.smtp')} atual={caminho} />
            )}
          </ul>

          {lista === null ? null : lista.length === 0 ? (
            pode('ParametroVer') && (
              <p className="casca__sem-marca">
                {t('nav.semMarca')} <a href="/marcas">{t('nav.cadastrarMarca')}</a>
              </p>
            )
          ) : (
            lista.map((marca) => (
              <BlocoDaMarca key={marca.chave} marca={marca} atual={caminho} />
            ))
          )}

          <ul className="casca__lista casca__lista--rodape">
            {/* Ajuda antes do catálogo: o catálogo é material de quem constrói,
                a ajuda é de quem opera — e depois de 30/09 quem opera é o cliente. */}
            <Item href="/ajuda" rotulo={t('nav.ajuda')} atual={caminho} />
            <Item href="/catalogo" rotulo={t('portal.catalogo')} atual={caminho} />
          </ul>
        </nav>

        <main className="casca__conteudo">{children}</main>
      </div>
    </div>
  )
}

/**
 * Um bloco por marca, colapsável.
 *
 * `<details>` em vez de estado próprio: o colapso já é acessível por teclado,
 * anunciado por leitor de tela e sobrevive sem JavaScript. Estado próprio aqui
 * seria reimplementar pior o que o navegador faz.
 */
function BlocoDaMarca({ marca, atual }: { marca: MarcaNaLista; atual: string }) {
  const { t } = useTranslation()
  const { pode } = useSessao()
  const chaveDoArmazem = `menu.marca.${marca.chave}`

  const [aberto, setAberto] = useState(() => {
    const guardado = localStorage.getItem(chaveDoArmazem)
    // Sem preferência guardada, abre a marca em que a pessoa já está.
    return guardado === null ? atual.startsWith(`/marcas/${marca.chave}`) : guardado === 'sim'
  })

  const base = `/marcas/${marca.chave}`

  return (
    <details
      className="casca__marca-bloco"
      open={aberto}
      onToggle={(evento) => {
        const agora = (evento.currentTarget as HTMLDetailsElement).open
        setAberto(agora)
        localStorage.setItem(chaveDoArmazem, agora ? 'sim' : 'nao')
      }}
    >
      <summary className="casca__marca-nome">{marca.nome}</summary>

      <ul className="casca__lista">
        <Item href={`${base}/assistentes`} rotulo={t('portal.assistentes')} atual={atual} />

        {/* Logo abaixo do assistente de propósito: quem acaba de mexer no
            comportamento precisa do console no caminho, não escondido. */}
        <Item href={`${base}/console`} rotulo={t('console.titulo')} atual={atual} />

        <Item href={`${base}/materiais`} rotulo={t('materiais.titulo')} atual={atual} />
        <Item href={`${base}/caixas`} rotulo={t('portal.caixas')} atual={atual} />
        <Item href={`${base}/cotacao`} rotulo={t('portal.cotacao')} atual={atual} />
        <Item href={`${base}/simulacao`} rotulo={t('portal.simulacao')} atual={atual} />
        <Item href={`${base}/orcamentos`} rotulo={t('portal.orcamentos')} atual={atual} />
        <Item href={`${base}/conhecimento`} rotulo={t('portal.conhecimento')} atual={atual} />
        <Item href={`${base}/roteamento`} rotulo={t('portal.roteamento')} atual={atual} />

        {/* Permissão própria, e não `ParametroVer` como os vizinhos: registro de
            entrega é dado pessoal com assinatura. */}
        {pode('EntregaVer') && (
          <Item href={`${base}/entregas`} rotulo={t('entregas.titulo')} atual={atual} />
        )}

        {pode('ArquivoVer') && (
          <Item href={`${base}/arquivos`} rotulo={t('arquivos.titulo')} atual={atual} />
        )}

        {/* `AuditoriaVer`, e não `EntregaVer`: o log diz para qual telefone cada
            documento foi. É evidência de conformidade, não operação de
            expedição — e o vizinho acima é outra coisa, apesar do nome. */}
        {pode('AuditoriaVer') && (
          <Item
            href={`${base}/entregas-de-documento`}
            rotulo={t('logDeEntrega.titulo')}
            atual={atual}
          />
        )}
      </ul>
    </details>
  )
}

/**
 * Item de menu é `<a>`, não botão com `onClick`.
 *
 * Link que navega precisa ser link: abre em nova aba, aparece no histórico e é
 * anunciado como link. Botão que troca `window.location` não faz nada disso.
 */
function Item({ href, rotulo, atual }: { href: string; rotulo: string; atual: string }) {
  // 🔴 Igualdade, não prefixo. Com `startsWith`, estar em
  // `/marcas/digiorgio/assistentes` marcava TAMBÉM o item `/marcas` — dois
  // `aria-current="page"` na mesma navegação, que é inválido e faz o leitor de
  // tela anunciar duas páginas atuais. Todo item do menu aponta para um caminho
  // exato, então a comparação exata é a correta.
  const ehAtual = atual === href || atual === `${href}/`

  return (
    <li>
      <a className="casca__item" href={href} aria-current={ehAtual ? 'page' : undefined}>
        {rotulo}
      </a>
    </li>
  )
}
